import dynamic from 'next/dynamic'
import RouteLink from 'next/link'
import React, { FC, useMemo } from 'react'

import {
  Box,
  Card,
  CardActionArea,
  CardContent,
  Divider,
  Grid
} from '@material-ui/core'
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles'
import { Skeleton } from '@material-ui/lab'

import { ContactSellerDialogProps } from '~/components/dialogs/ContactSellerDialog/ContactSellerDialog'
import TriggerDialog from '~/containers/TriggerDialog'

import { transformZeroToValue } from '~/utils/functions'
import Paths from '~/utils/paths'

import { getSpecifications, IS_RV } from '~/brands'
import { Listing } from '~/models/Listing/interface'
import { ListingModel } from '~/models/Listing/model'

import ExtendedButton from '../../shared/ExtendedButton'
import Text from '../../shared/Text'
import SimpleCard from '../SimpleCard'

import ProductMedia from './ProductMedia'

const ContactSellerDialog = dynamic(() =>
  import('~/components/dialogs/ContactSellerDialog/ContactSellerDialog')
)

const useStyles = makeStyles(
  (_theme: Theme) =>
    createStyles({
      card: {
        padding: 0,
        height: (props: { featured: boolean; fullHeight: boolean }) =>
          props.fullHeight ? '100%' : 'auto',
        boxShadow: (props: { featured: boolean; fullHeight: boolean }) =>
          props.featured
            ? `-10px 30px 50px ${IS_RV ? '#098ACB' : '#27A4BF'} !important`
            : null,
        border: (props: { featured: boolean; fullHeight: boolean }) =>
          props.featured ? '2px solid #232323' : null
      },
      cardAction: {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'stretch'
      },
      cardContent: {
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column'
      }
    }),
  { name: 'ProductListCard' }
)

type Props = {
  data: Listing
  onFavorite?: (favorite: boolean) => void
  fullHeight?: boolean
  dealerBox?: boolean
  loading?: boolean
  featured?: boolean
}

const placeholder = new Array(4).fill({})

const ProductListCard: FC<Props> = ({
  data,
  onFavorite,
  fullHeight = true,
  loading,
  featured
}) => {
  const classes = useStyles({ fullHeight, featured })
  const [cover] = data?.images || []

  const name = data?.short_title || ListingModel.createTitle(data)

  const specifications = useMemo(() => getSpecifications(data, 0.8), [data])

  const dialogOnClick = (e) => {
    if (!e) return
    e.preventDefault()
    e.stopPropagation()
  }

  const coverUrl = cover?.url || data?.cover_url
  const specificationsData = loading ? placeholder : specifications

  return (
    <Card
      component={SimpleCard}
      className={classes.card}
      style={{ pointerEvents: loading ? 'none' : 'auto' }}
    >
      <RouteLink
        href={Paths.details.buildDetails({ slug: String(data.slug) })}
        passHref
        prefetch={false}
      >
        <CardActionArea className={classes.cardAction}>
          <ProductMedia
            style={{
              height: 350,
              width: '100%',
              minWidth: 350
            }}
            onFavorite={onFavorite}
            cover={coverUrl}
            favorite={data?.favorite}
            isPlus={data?.package_plus}
            isPremium={data?.package_premium}
            featured={featured}
            loading={loading}
            productId={data?.id}
            alt={name}
            variant="list"
          />

          <CardContent className={classes.cardContent}>
            <Text
              mt={0}
              mb={1}
              variant="h5"
              component="h2"
              fontFamily="Prompt, sans-serif"
              fontSize={18}
              fontWeight={400}
            >
              {loading ? <Skeleton width={300} /> : name}
            </Text>

            {loading ? (
              <Skeleton variant="text" width={200} />
            ) : (
              <Text
                mt="auto"
                color="primary"
                variant="h5"
                fontSize={18}
                fontFamily="Prompt, sans-serif"
              >
                {transformZeroToValue(data?.price)}
              </Text>
            )}

            <Box my={2}>
              <Divider />
            </Box>

            <Box flex={1} mb={1}>
              <Grid container spacing={1}>
                {specificationsData.map(({ icon, value }, index) => (
                  <Grid item xs={6} key={index}>
                    {loading ? (
                      <Skeleton variant="text" width="100%" />
                    ) : (
                      <Box display="flex" alignItems="center" height="100%">
                        <Box
                          display="flex"
                          minWidth={32}
                          maxWidth={32}
                          justifyContent="center"
                          alignItems="center"
                        >
                          {icon}
                        </Box>

                        <Text noWrap fontSize={14} ml={1}>
                          {value}
                        </Text>
                      </Box>
                    )}
                  </Grid>
                ))}
              </Grid>
            </Box>

            <Grid container spacing={2}>
              <Grid item xs={6}>
                <ExtendedButton fullWidth size="medium" variant="outlined">
                  View Details
                </ExtendedButton>
              </Grid>

              <Grid item xs={6}>
                <TriggerDialog<Partial<ContactSellerDialogProps>>
                  dialog={ContactSellerDialog}
                  dialogProps={{
                    listingId: data?.id,
                    listingPrice: data?.price
                  }}
                  onClick={dialogOnClick}
                >
                  <ExtendedButton fullWidth size="medium">
                    Contact seller
                  </ExtendedButton>
                </TriggerDialog>
              </Grid>
            </Grid>
          </CardContent>
        </CardActionArea>
      </RouteLink>
    </Card>
  )
}

export default ProductListCard
